import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, MonitorPlay, Rabbit } from "lucide-react";
import { api } from "../lib/api";
import { CurrencyIcon, currencyLabel } from "../lib/currencyDisplay";

export default function Sounds() {
  const [data, setData] = useState(null);

  const siteOrigin = window.location.origin;
  const interactUrl = `${siteOrigin}/redeems`;
  const overlayUrl = `${siteOrigin}/overlay`;

  const load = async () => {
    const { data: response } = await api.get("/interactions");
    setData(response);
  };

  useEffect(() => { load(); }, []);

  const currencyMap = useMemo(() => {
    const map = {};
    (data?.currencies || []).forEach((currency) => { map[currency.id] = currency; });
    return map;
  }, [data]);

  const categoryMap = useMemo(() => {
    const map = {};
    (data?.interaction_categories || []).forEach((category) => { map[category.slug] = category; });
    return map;
  }, [data]);

  const publicInteractions = data?.interaction_definitions || [];

  return (
    <div className="space-y-6" data-testid="sounds-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Viewer-facing display</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">Viewer Redeems</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Viewer-facing page where enabled public interactions appear.
          </p>
        </div>
        <a
          href={interactUrl}
          target="_blank"
          rel="noreferrer"
          className="border border-[hsl(var(--border))] text-sm px-4 py-2 rounded flex items-center gap-2 hover:border-[hsl(var(--primary))]"
        >
          <ExternalLink className="w-4 h-4" /> Open Viewer Redeems
        </a>
      </div>

      <section className="surface p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded bg-[hsl(var(--surface-2))] flex items-center justify-center shrink-0">
            <Rabbit className="w-4 h-4 text-[hsl(var(--primary))]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Public Interaction Display</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Create and configure interactions in <Link to="/app/interactions" className="underline text-[hsl(var(--primary))]">Interaction Builder</Link>. This page only previews what viewers can see.
            </p>
          </div>
        </div>
      </section>

      <section className="surface p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded bg-[hsl(var(--surface-2))] flex items-center justify-center shrink-0">
            <MonitorPlay className="w-4 h-4 text-[hsl(var(--primary))]" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold tracking-tight">Overlay Source</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Use this full overlay page for video media, chroma key, and legacy audio alerts.</p>
            <code className="block mt-3 font-mono text-xs text-[hsl(var(--primary))] break-all" data-testid="overlay-url">{overlayUrl}</code>
            <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">1920x1080 - transparent - enable audio</div>
          </div>
        </div>
      </section>

      <section className="surface overflow-hidden">
        <div className="p-5 border-b border-[hsl(var(--border))]">
          <div className="label-eyebrow">Currently public</div>
          <h2 className="text-lg font-semibold tracking-tight mt-1">Enabled Public Interactions</h2>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {!data && <div className="text-sm text-[hsl(var(--muted-foreground))]">Loading viewer redeems...</div>}
          {data && publicInteractions.length === 0 && (
            <div className="text-sm text-[hsl(var(--muted-foreground))]">No enabled public interactions yet.</div>
          )}
          {publicInteractions.map((item) => {
            const accentColor = item.accent_color || "#0A84FF";
            const currency = currencyMap[item.cost_type];
            const tags = (item.category_tags || []).map((slug) => categoryMap[slug] || { slug, name: slug, color: "hsl(var(--muted-foreground))" });
            return (
              <div key={item.id} className="border border-[hsl(var(--border))] rounded p-4 bg-[hsl(var(--surface-2))]" style={{ borderLeft: `3px solid ${accentColor}` }}>
                <div className="flex items-start gap-3">
                  <CurrencyIcon currency={{ icon: item.icon, color: accentColor }} className="w-4 h-4 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{item.name}</div>
                    {item.description && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{item.description}</div>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {tags.map((tag) => (
                    <span key={tag.slug} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border" style={{ color: tag.color, borderColor: tag.color }}>
                      {tag.name}
                    </span>
                  ))}
                </div>
                <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
                  Cost: <span className="font-mono text-white">{item.cost_amount}</span> {currencyLabel(currency || { id: item.cost_type })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

    </div>
  );
}
